import { useMemo } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import { useGetTruckColumnsQuery, UserPermission } from 'data/__generated__';

import { Loader } from 'components/Loader';
import { TotemLabel } from 'components/TotemLabel';
import { TotemSelect } from 'components/TotemSelect';

import { UserDetailsFormData } from 'pages/Users/UserDetails/UserDetails';

const DELIVERER_USER_GROUPS: string[] = [UserPermission.Deliverer, UserPermission.ExternalDeliverer];

export const DefaultTruckColumnSelector = () => {
    const { setValue, getValues } = useFormContext<UserDetailsFormData>();
    const permissions = useWatch<UserDetailsFormData, 'permissions'>({ name: 'permissions' }) ?? [];
    const defaultTruckColumnId = useWatch<UserDetailsFormData, 'defaultTruckColumnId'>({
        name: 'defaultTruckColumnId',
        defaultValue: getValues('defaultTruckColumnId') ?? null,
    });

    const isDeliverer = permissions.some((permission) =>
        permission.userGroups.some((userGroup) => DELIVERER_USER_GROUPS.includes(userGroup)),
    );

    const { data, loading } = useGetTruckColumnsQuery({ skip: !isDeliverer });

    const options = useMemo(() => {
        if (!data?.truckColumns) return [];
        return data.truckColumns
            .map((column) => ({
                value: column._id,
                label: `${column.siteName} / ${column.name}`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [data]);

    if (!isDeliverer) {
        return null;
    }

    if (loading) {
        return (
            <>
                <TotemLabel>Colonne camion par défaut</TotemLabel>
                <Loader />
            </>
        );
    }

    return (
        <TotemSelect
            label="Colonne camion par défaut"
            placeholder="Sélectionner la colonne du camion affecté à ce livreur"
            value={defaultTruckColumnId ?? null}
            options={options}
            onChange={(value) =>
                setValue('defaultTruckColumnId', value ?? null, { shouldDirty: true, shouldValidate: false })
            }
            isClearable
            dataTest="default-truck-column-select"
        />
    );
};
